/**
 * Generate React Flow elements (nodes and edges) from a file tree structure
 * @param {Array} tree - Array of tree nodes (from buildFileTree)
 * @returns {Object} Object with nodes and edges arrays matching elements.js format
 */
export function generateElementsFromTree(tree) {
  const nodes = [];
  const edges = [];

  /**
   * Recursively traverse the tree and create nodes and edges
   * @param {Array} children - Array of child nodes
   * @param {string} parentId - ID of parent node (null for root level)
   */
  function traverseTree(children, parentId = null) {
    if (!children || !Array.isArray(children)) {
      return;
    }

    children.forEach((node) => {
      // Check if node has children (for folders or files with children like Python files with functions)
      const hasChildren = node.children && (
        Array.isArray(node.children) ? node.children.length > 0 : Object.keys(node.children).length > 0
      );
      
      // Get children array for universal node
      const childrenArray = Array.isArray(node.children) ? node.children : (node.children ? Object.values(node.children) : []);
      
      // Create universal node - all nodes use universal type
      // Determine className based on node type
      let className;
      if (node.type === 'function') {
        className = 'function-node';
      } else if (node.type === 'method') {
        className = 'method-node';
      } else if (node.type === 'class') {
        className = 'class-node';
      } else if (node.type === 'folder') {
        className = 'folder-node';
      }
      
      const nodeData = {
        id: node.id,
        type: 'universal',
        className: className,
        data: {
          label: node.name,
          path: node.path,
          hasChildren: hasChildren,
          children: childrenArray, // Include children array so UniversalNode can determine folder vs file
          originalNode: node, // Keep reference to original node for compatibility
        },
      };

      nodes.push(nodeData);

      // Create edge from parent to this node
      if (parentId !== null) {
        // Determine edge className for dashed styling
        // Edges to classes and methods should be dashed
        let edgeClassName = null;
        if (node.type === 'class') {
          edgeClassName = 'class-edge';
        } else if (node.type === 'method') {
          edgeClassName = 'method-edge';
        }
        
        edges.push({
          id: `e${parentId}-${node.id}`,
          source: parentId,
          target: node.id,
          type: 'step',
          className: edgeClassName,
        });
      }

      // Recursively process children for folders, classes, and files with children
      if (hasChildren && (node.type === 'folder' || node.type === 'class' || (node.type === 'file' && node.children))) {
        traverseTree(node.children, node.id);
      }
    });
  }

  // Start traversal from root level
  traverseTree(tree);

  return { nodes, edges };
}

